import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import dayjs from "dayjs";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { setHeaderCollapse } from "../../../../core/data/redux/commonSlice";
import { AuthContext } from "../../../../context/authcontext";
import { all_routes } from "../../../router/all_routes";
import { useMoneyFormat } from "../../../../components/currencyFormat";
import TaskDetailModal from "../../../../components/TaskDetailModal";
import { API_BASE_URL } from "../../../../environment";
import { getAuthHeaders } from "../../../../utils/authUtils";
import { getLifecycleLabels, LifecycleLabels } from "../../../../utils/lifecycleUtils";
import { HeaderAction, OptionDropdown, DropdownOption } from "../common";
import { ChartMetric, GRANULARITY_NOUNS, GranularityToggle, MetricToggle } from "../chartControls";
import { ENTITY_ICONS } from "../entityIcons";
import DashboardFilterDropdown, {
  ActiveFilterChips,
  useDashboardFilters,
  hasFilterChips,
  toApiFilters,
} from "../filters/DashboardFilterDropdown";
import {
  SkeletonBlock,
  RevenueChartSkeleton,
  KpiCardSkeleton,
  TopDealsSkeleton,
  DealsOverviewSkeleton,
} from "../dashboard/DashboardSkeletons";
import { MoneyBagIcon, SectionTitle, EmptyState } from "../dashboard/DashboardParts";
import { palette, statusClassName } from "../dashboard/constants";
import {
  ActivityPeriod,
  RecordKind,
  SalesUserRole,
  SalesUserStats,
  fetchSalesUserStats,
} from "./salesUserDashboardService";
import "../dashboard/newDashboard.css";
import KpiCarousel from "./KpiCarousel";
import "./salesUserDashboard.css";

const route = all_routes;

const NAME_MAX_LENGTH = 34;

const TREND_COLORS = ["#E41F07", "#FFA201", "#3F3BD9"];

const STAGE_COLORS = ["#3F3BD9", "#06AED4", "#FFA201", "#2DB05D", "#E0338F", "#8E1E9C", "#E41F07", "#0E9384"];

const OUTCOME_COLORS = { won: "#2DB05D", lost: "#E8432F", open: "#FDAF22" };

const TEAM_ENTITY_COLORS: Record<string, string> = {
  company: "#06AED4",
  contact: "#8E1E9C",
  lead: "#E0338F",
  pipeline: "#FFA201",
  deal: "#3F3BD9",
};

const RECORD_ROUTES: Record<RecordKind, string> = {
  leads: "/leads",
  pipelines: "/pipelines",
  deals: "/deals",
};

const formatNumber = (value: number) => (Number.isFinite(value) ? value.toLocaleString() : "0");

const parseGrowth = (growth?: string | null) => {
  const parsed = parseFloat(String(growth ?? ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const formatGrowth = (growth?: string | null) => {
  const text = String(growth ?? "").trim();
  if (!text) return "0%";
  if (/^[+-]/.test(text)) return text;
  return (parseGrowth(text) ?? 0) > 0 ? `+${text}` : text;
};

const growthBadgeClass = (growth?: string | null) =>
  (parseGrowth(growth) ?? 0) < 0 ? "badge-soft-danger" : "badge-soft-success";

const formatDate = (value: string, format = "DD MMM YYYY") => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(format) : value;
};

const formatActivityLabel = (label: string, period: ActivityPeriod) => {
  if (period === "by_year") return label;
  const parsed = dayjs(label);
  if (/^\d{4}-\d{2}/.test(label) && parsed.isValid()) {
    return parsed.format(period === "by_week" ? "DD MMM" : "MMM YYYY");
  }
  const monthYear = label.trim().match(/^([A-Za-z]+)\s+(\d{4})$/);
  if (period === "by_month" && monthYear) return `${monthYear[1].slice(0, 3)} ${monthYear[2]}`;
  return period === "by_month" ? label.slice(0, 3) : label;
};

const pluralOf = (kind: RecordKind, names: LifecycleLabels) =>
  kind === "deals" ? names.deals : kind === "pipelines" ? names.pipelines : names.leads;

const entityLabel = (entity: string, names: LifecycleLabels) => {
  switch (entity.toLowerCase()) {
    case "company":
      return "Companies";
    case "contact":
      return "Contacts";
    case "lead":
      return names.leads;
    case "pipeline":
      return names.pipelines;
    case "deal":
      return names.deals;
    default:
      return entity;
  }
};

const taskStatusClass = (status: string) => {
  const normalized = status.trim().toLowerCase();
  if (["completed", "done", "closed"].includes(normalized)) return "badge-soft-success";
  if (["overdue", "cancelled", "canceled"].includes(normalized)) return "badge-soft-danger";
  if (["in progress", "in_progress", "pending"].includes(normalized)) return "badge-soft-warning";
  return "badge-soft-info";
};

const TruncatedLink = ({ to, text }: { to: string | null; text: string }) => {
  const truncated = text.length > NAME_MAX_LENGTH;
  const label = truncated ? `${text.slice(0, NAME_MAX_LENGTH).trimEnd()}...` : text;
  const content = to ? (
    <Link to={to} className="fw-medium text-dark">
      {label}
    </Link>
  ) : (
    <span className="fw-medium text-dark">{label}</span>
  );
  if (!truncated) return content;
  return (
    <OverlayTrigger placement="top" overlay={<Tooltip id={`sud-${to ?? text}`}>{text}</Tooltip>}>
      {content}
    </OverlayTrigger>
  );
};

const TableSkeletonRows = ({ columns }: { columns: number }) => (
  <>
    {[140, 112, 158, 124, 134].map((width, index) => (
      <tr key={index}>
        {Array.from({ length: columns }, (_, column) => (
          <td key={column}>
            <SkeletonBlock width={column === 0 ? width : 64} height={12} />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const SalesUserDashboard = () => {
  const dispatch = useDispatch();
  const headerCollapse = useSelector((state: any) => state.commonSlice.headerCollapse);
  const { auth, selectedTenantId, lifecycleTypeNames } = useContext(AuthContext);
  const names = useMemo(() => getLifecycleLabels(lifecycleTypeNames), [lifecycleTypeNames]);

  const tenant = useMemo(
    () => (auth?.tenants ?? []).find((item: any) => String(item?.tenant_id) === String(selectedTenantId)),
    [auth, selectedTenantId]
  );
  const salesUserId = Number(auth?.user_id ?? auth?.id) || null;
  const salesUserRole: SalesUserRole = tenant?.team_manager ? "team_manager" : "normal";
  const isTeamManager = salesUserRole === "team_manager";

  const [data, setData] = useState<SalesUserStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const { filters, setFilters, defaults: defaultFilters } = useDashboardFilters();

  const [trendPeriod, setTrendPeriod] = useState<ActivityPeriod>("by_month");
  const [trendMetric, setTrendMetric] = useState<ChartMetric>("count");
  const [stageKind, setStageKind] = useState<RecordKind>("leads");
  const [stageMetric, setStageMetric] = useState<ChartMetric>("count");
  const [teamMetric, setTeamMetric] = useState<ChartMetric>("count");
  const [recordKind, setRecordKind] = useState<RecordKind>("deals");

  const apiFilters = useMemo(
    () => ({ ...toApiFilters(filters), assignedUserId: null, salesUserId, salesUserRole }),
    [filters, salesUserId, salesUserRole]
  );
  const money = useMoneyFormat(filters.currencyCode);
  const formatCurrency = money.compact;

  useEffect(() => {
    setData(null);
  }, [selectedTenantId]);

  useEffect(() => {
    if (!selectedTenantId || !salesUserId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchSalesUserStats(selectedTenantId, apiFilters)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Failed to load dashboard");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTenantId, salesUserId, reloadKey, apiFilters]);

  const reload = () => setReloadKey((key) => key + 1);

  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskDetails, setTaskDetails] = useState<any>(null);
  const [taskLoading, setTaskLoading] = useState(false);
  const activeTaskRef = useRef<number | null>(null);

  const openTask = async (taskId: number) => {
    activeTaskRef.current = taskId;
    setTaskDetails(null);
    setTaskModalOpen(true);
    setTaskLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/task/${taskId}/`, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error(`Failed to load task (${response.status})`);
      const task = await response.json();
      if (activeTaskRef.current === taskId) setTaskDetails(task);
    } catch {
      if (activeTaskRef.current === taskId) setTaskDetails(null);
    } finally {
      if (activeTaskRef.current === taskId) setTaskLoading(false);
    }
  };

  const closeTask = () => {
    activeTaskRef.current = null;
    setTaskModalOpen(false);
    setTaskDetails(null);
    setTaskLoading(false);
  };
  const loaded = loading ? null : data;

  const recordOptions = useMemo<DropdownOption<RecordKind>[]>(
    () => [
      { value: "leads", label: names.leads },
      { value: "pipelines", label: names.pipelines },
      { value: "deals", label: names.deals },
    ],
    [names]
  );

  const scorecards = data?.scorecards;
  const kpiCards = scorecards
    ? [
        {
          key: "sales",
          label: "Total Sales",
          value: formatCurrency(scorecards.totalSales),
          growth: scorecards.totalSalesGrowth,
          caption: "vs Last Period",
          icon: "moneybag",
          gradient: ["#E41F07", "#FF5A43"],
        },
        {
          key: "revenue",
          label: "Total Revenue",
          value: formatCurrency(scorecards.totalRevenue),
          growth: scorecards.totalRevenueGrowth,
          caption: "vs Last Period",
          icon: "ti ti-coin-filled",
          gradient: ["#2DB05D", "#5CD48A"],
        },
        {
          key: "activities",
          label: "Total Activities",
          value: formatNumber(scorecards.totalActivities),
          growth: scorecards.totalActivitiesGrowth,
          caption: "vs Last Period",
          icon: "ti ti-activity",
          gradient: ["#3F3BD9", "#6E6BF2"],
        },
        {
          key: "lead",
          label: names.leads,
          value: formatNumber(scorecards.lead.count),
          pill: formatCurrency(scorecards.lead.amount),
          caption: "Total Value",
          icon: ENTITY_ICONS.lead,
          gradient: ["#06AED4", "#4FD1EA"],
        },
        {
          key: "pipeline",
          label: names.pipelines,
          value: formatNumber(scorecards.pipeline.count),
          pill: formatCurrency(scorecards.pipeline.amount),
          caption: "Total Value",
          icon: ENTITY_ICONS.pipeline,
          gradient: ["#FFA201", "#FFC14D"],
        },
        {
          key: "company",
          label: "Companies",
          value: formatNumber(scorecards.company.count),
          icon: ENTITY_ICONS.company,
          gradient: ["#0E9384", "#3CC4B1"],
        },
        {
          key: "contact",
          label: "Contacts",
          value: formatNumber(scorecards.contact.count),
          icon: ENTITY_ICONS.contact,
          gradient: ["#8E1E9C", "#B84FC6"],
        },
      ]
    : [];

  const trendPoints = useMemo(() => data?.activity[trendPeriod] ?? [], [data, trendPeriod]);
  const trendHasData = trendPoints.some(
    (point) => point.leadCount + point.pipelineCount + point.dealCount + point.leadAmount + point.dealAmount + point.pipelineAmount > 0
  );

  const trendSeries = useMemo(
    () =>
      trendMetric === "count"
        ? [
            { name: names.leads, data: trendPoints.map((point) => point.leadCount) },
            { name: names.pipelines, data: trendPoints.map((point) => point.pipelineCount) },
            { name: names.deals, data: trendPoints.map((point) => point.dealCount) },
          ]
        : [
            { name: names.leads, data: trendPoints.map((point) => point.leadAmount) },
            { name: names.pipelines, data: trendPoints.map((point) => point.pipelineAmount) },
            { name: names.deals, data: trendPoints.map((point) => point.dealAmount) },
          ],
    [trendPoints, trendMetric, names]
  );

  const trendOptions = useMemo<ApexOptions>(() => {
    const formatValue = (val: number) => (trendMetric === "count" ? formatNumber(Math.round(val)) : formatCurrency(val));
    return {
      chart: { type: "bar", toolbar: { show: false }, foreColor: palette.axis },
      colors: TREND_COLORS,
      plotOptions: { bar: { columnWidth: "55%", borderRadius: 3, borderRadiusApplication: "end" } },
      dataLabels: { enabled: false },
      stroke: { show: true, width: 2, colors: ["transparent"] },
      legend: { show: false },
      grid: { borderColor: palette.grid, strokeDashArray: 5 },
      xaxis: {
        categories: trendPoints.map((point) => formatActivityLabel(point.label, trendPeriod)),
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { rotate: 0, hideOverlappingLabels: true, style: { fontSize: "11px" } },
      },
      yaxis: { min: 0, forceNiceScale: true, labels: { formatter: formatValue, style: { fontSize: "11px" } } },
      tooltip: { shared: true, intersect: false, y: { formatter: formatValue } },
    };
  }, [trendPoints, trendPeriod, trendMetric, formatCurrency]);

  const outcomes = scorecards
    ? [
        { key: "won", label: `${names.deals} Won`, total: scorecards.wonDeals, color: OUTCOME_COLORS.won },
        { key: "open", label: `Open ${names.deals}`, total: scorecards.openDeals, color: OUTCOME_COLORS.open },
        { key: "lost", label: `${names.deals} Lost`, total: scorecards.lostDeals, color: OUTCOME_COLORS.lost },
      ]
    : [];
  const outcomeTotal = outcomes.reduce((sum, outcome) => sum + outcome.total.count, 0);
  const closedDeals = (scorecards?.wonDeals.count ?? 0) + (scorecards?.lostDeals.count ?? 0);
  const winRate = closedDeals > 0 ? Math.round(((scorecards?.wonDeals.count ?? 0) / closedDeals) * 100) : 0;

  const stages = useMemo(
    () => (data?.stages[stageKind] ?? []).filter((stage) => stage.stageName),
    [data, stageKind]
  );
  const stageValue = (stage: { count: number; amount: number }) =>
    stageMetric === "count" ? stage.count : stage.amount;
  const stageMax = Math.max(0, ...stages.map(stageValue));

  const records = data?.recent[recordKind] ?? [];
  const showSource = recordKind === "pipelines";

  const tasks = data?.tasks ?? [];
  const teams = data?.teams ?? [];
  const teamEntities = useMemo(() => data?.teamEntities ?? [], [data]);

  const teamEntityKeys = useMemo(() => {
    const order = Object.keys(TEAM_ENTITY_COLORS);
    const rank = (key: string) => (order.includes(key) ? order.indexOf(key) : order.length);
    const keys = Array.from(
      new Set(teamEntities.flatMap((team) => team.entities.map((entity) => entity.entity.toLowerCase())))
    )
      .filter(Boolean)
      .sort((a, b) => rank(a) - rank(b));
    if (teamMetric === "count") return keys;
    return keys.filter((key) =>
      teamEntities.some((team) =>
        team.entities.some((entity) => entity.entity.toLowerCase() === key && entity.amount > 0)
      )
    );
  }, [teamEntities, teamMetric]);

  const teamEntitySeries = useMemo(
    () =>
      teamEntityKeys.map((key) => ({
        name: entityLabel(key, names),
        data: teamEntities.map((team) => {
          const entity = team.entities.find((item) => item.entity.toLowerCase() === key);
          return teamMetric === "count" ? entity?.count ?? 0 : entity?.amount ?? 0;
        }),
      })),
    [teamEntityKeys, teamEntities, names, teamMetric]
  );

  const teamEntityOptions = useMemo<ApexOptions>(
    () => ({
      chart: { type: "bar", stacked: true, toolbar: { show: false }, foreColor: palette.axis },
      colors: teamEntityKeys.map((key) => TEAM_ENTITY_COLORS[key] ?? "#B0B7C3"),
      plotOptions: { bar: { horizontal: true, barHeight: "55%", borderRadius: 3, borderRadiusWhenStacked: "last" } },
      dataLabels: { enabled: false },
      legend: { show: true, position: "top", horizontalAlign: "left", markers: { size: 5 }, itemMargin: { horizontal: 6 } },
      grid: { borderColor: palette.grid, strokeDashArray: 5 },
      xaxis: {
        categories: teamEntities.map((team) => team.teamName || "Unnamed Team"),
        labels: {
          formatter: (val: string) =>
            teamMetric === "count" ? formatNumber(Math.round(Number(val))) : formatCurrency(Number(val)),
          style: { fontSize: "11px" },
        },
      },
      yaxis: { labels: { maxWidth: 140, style: { fontSize: "12px" } } },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (_val: number, opts?: any) => {
            const key = teamEntityKeys[opts?.seriesIndex ?? 0];
            const entity = teamEntities[opts?.dataPointIndex ?? 0]?.entities.find(
              (item) => item.entity.toLowerCase() === key
            );
            const count = entity?.count ?? 0;
            const amount = entity?.amount ?? 0;
            if (teamMetric === "amount") return `${formatCurrency(amount)} · ${formatNumber(count)}`;
            return amount > 0 ? `${formatNumber(count)} · ${formatCurrency(amount)}` : formatNumber(count);
          },
        },
      },
    }),
    [teamEntities, teamEntityKeys, formatCurrency, teamMetric]
  );

  const showTeams = teams.length > 0 || teamEntities.length > 0;

  const trendLegend = [
    { key: "lead", label: names.leads, color: TREND_COLORS[0] },
    { key: "pipeline", label: names.pipelines, color: TREND_COLORS[1] },
    { key: "deal", label: names.deals, color: TREND_COLORS[2] },
  ];
  const trendCaption = `${trendMetric === "count" ? "Number" : "Total value"} of ${names.leads}, ${names.pipelines} & ${
    names.deals
  } created per ${GRANULARITY_NOUNS[trendPeriod]}`;

  const header = (
    <>
      <div
        className={`d-flex align-items-center justify-content-between gap-2 flex-wrap ${
          hasFilterChips(filters, defaultFilters, true) ? "mb-3" : "mb-4"
        }`}
      >
        <div className="d-flex align-items-center gap-2">
          <h4 className="mb-0">Dashboard</h4>
          {isTeamManager && <span className="badge sud-role-badge">Team Manager</span>}
        </div>
        <div className="gap-2 d-flex align-items-center flex-wrap">
          {selectedTenantId && (
            <DashboardFilterDropdown
              title="Dashboard Filters"
              filters={filters}
              onChange={setFilters}
              loading={loading}
              hiddenSections={["user", "status"]}
              showDefaultDateRange
            />
          )}
          <HeaderAction label="Refresh" icon={`ti ti-refresh${loading ? " nd-spin" : ""}`} onClick={reload} />
          <HeaderAction
            label={headerCollapse ? "Expand" : "Collapse"}
            icon="ti ti-transition-top"
            onClick={() => dispatch(setHeaderCollapse(!headerCollapse))}
          />
        </div>
      </div>
      <ActiveFilterChips filters={filters} onChange={setFilters} disabled={loading} showDefaultDateRange />
      {loading && (
        <span className="visually-hidden" role="status">
          Loading dashboard
        </span>
      )}
    </>
  );

  if (!selectedTenantId) {
    return (
      <div className="page-wrapper new-dashboard sales-user-dashboard">
        <div className="content pb-0">
          {header}
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="ti ti-building-community fs-1 text-muted mb-3" />
              <h5>Select a Tenant</h5>
              <p className="text-muted mb-0">Please select a tenant to view the dashboard.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data && error && !loading) {
    return (
      <div className="page-wrapper new-dashboard sales-user-dashboard">
        <div className="content pb-0">
          {header}
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="ti ti-alert-circle fs-1 text-danger mb-3" />
              <h5>Unable to load dashboard</h5>
              <p className="text-muted mb-3">{error}</p>
              <button type="button" className="btn btn-outline-light border" onClick={reload}>
                <i className="ti ti-refresh me-1" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper new-dashboard sales-user-dashboard">
      <div className="content pb-0">
        {header}

        {error && (
          <div className="alert alert-danger d-flex align-items-center justify-content-between gap-2 mb-4">
            <span>{error}</span>
            <button type="button" className="btn btn-sm btn-outline-light" onClick={reload}>
              Try Again
            </button>
          </div>
        )}

        <div aria-busy={!loaded}>
          {!loaded ? (
            <div className="row nd-section">
              {Array.from({ length: 4 }, (_, index) => (
                <div className="col-xl-3 col-sm-6 d-flex" key={index}>
                  <KpiCardSkeleton />
                </div>
              ))}
            </div>
          ) : (
            <div className="nd-section">
              <KpiCarousel label="summary cards">
                {kpiCards.map((card) => {
                  const hasFooter = card.growth !== undefined || card.pill !== undefined;
                  return (
                  <div className="kpi-card-wrap flex-fill" key={card.key}>
                    <div
                      className={`card kpi-card h-100 mb-0${hasFooter ? "" : " sud-kpi-compact"}`}
                      style={
                        hasFooter
                          ? undefined
                          : {
                              background: `radial-gradient(circle at 100% 100%, ${card.gradient[0]}1A 0, #fff 65%)`,
                            }
                      }
                    >
                      <div className="card-body">
                        <p className="fw-medium mb-1">{card.label}</p>
                        <h4 className={hasFooter ? "mb-3" : "mb-2"}>{card.value}</h4>
                        {!hasFooter && (
                          <>
                            <span
                              className="sud-kpi-accent"
                              style={{ background: `linear-gradient(90deg, ${card.gradient[0]}, ${card.gradient[1]})` }}
                            />
                            <i
                              className={`${card.icon} sud-kpi-watermark`}
                              style={{ color: card.gradient[0] }}
                              aria-hidden="true"
                            />
                          </>
                        )}
                        {hasFooter && (
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          {card.growth !== undefined ? (
                            <span
                              className={`d-inline-flex align-items-center badge rounded-pill border-0 ${growthBadgeClass(
                                card.growth
                              )}`}
                            >
                              {formatGrowth(card.growth)}
                            </span>
                          ) : (
                            <span className="d-inline-flex align-items-center badge rounded-pill sud-kpi-pill border-0">
                              {card.pill}
                            </span>
                          )}
                          <p className="text-dark mb-0">{card.caption}</p>
                        </div>
                        )}
                      </div>
                    </div>
                    <span
                      className="kpi-icon"
                      style={{ background: `linear-gradient(180deg, ${card.gradient[0]}, ${card.gradient[1]})` }}
                    >
                      {card.icon === "moneybag" ? (
                        <MoneyBagIcon color={card.gradient[0]} symbol={money.symbol} />
                      ) : (
                        <i className={card.icon} />
                      )}
                    </span>
                  </div>
                  );
                })}
              </KpiCarousel>
            </div>
          )}

          <div className="row nd-section">
            <div className="col-xxl-8 col-xl-7 d-flex">
              <div className="card flex-fill">
                <div className="card-body pb-0">
                  <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
                    <div className="min-w-0">
                      <SectionTitle title="Performance Trend" />
                      <p className="sud-chart-caption mb-0">{trendCaption}</p>
                    </div>
                    <GranularityToggle value={trendPeriod} onChange={setTrendPeriod} />
                  </div>
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                    <MetricToggle label="Show" value={trendMetric} onChange={setTrendMetric} />
                    <div className="d-flex align-items-center flex-wrap gap-3 sud-chart-legend">
                      {trendLegend.map((item) => (
                        <span key={item.key} className="d-inline-flex align-items-center">
                          <i className="ti ti-square-rounded-filled me-1" style={{ color: item.color }} />
                          {item.label}
                        </span>
                      ))}
                    </div>
                  </div>
                  {!loaded ? (
                    <RevenueChartSkeleton />
                  ) : trendHasData ? (
                    <Chart options={trendOptions} series={trendSeries} type="bar" height={340} />
                  ) : (
                    <EmptyState message="No activity recorded for this period" />
                  )}
                </div>
              </div>
            </div>

            <div className="col-xxl-4 col-xl-5 d-flex">
              <div className="card flex-fill">
                <div className="card-body d-flex flex-column">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                    <SectionTitle title={`${names.deals} Overview`} />
                    <Link to={route.deals} className="btn btn-sm btn-icon btn-outline-light border">
                      <i className="ti ti-arrow-right" />
                    </Link>
                  </div>
                  {!loaded || !scorecards ? (
                    <DealsOverviewSkeleton />
                  ) : (
                    <>
                      <div className="progress-stacked deals-progress gap-1 mb-3">
                        {outcomeTotal > 0 ? (
                          outcomes.map((outcome) => {
                            const width = (outcome.total.count / outcomeTotal) * 100;
                            return width > 0 ? (
                              <div
                                key={outcome.key}
                                className="progress-bar"
                                role="progressbar"
                                style={{ width: `${width}%`, backgroundColor: outcome.color }}
                                aria-valuenow={width}
                                aria-valuemin={0}
                                aria-valuemax={100}
                              />
                            ) : null;
                          })
                        ) : (
                          <div className="progress-bar nd-progress-empty" role="progressbar" style={{ width: "100%" }} />
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
                        <h4 className="mb-0">{formatNumber(scorecards.deal.count)}</h4>
                        <p className="mb-0">
                          {names.deals} · {formatCurrency(scorecards.deal.amount)}
                        </p>
                      </div>
                      <div className="mb-3">
                        {outcomes.map((outcome, index) => (
                          <div
                            key={outcome.key}
                            className={`p-2 d-flex align-items-center justify-content-between gap-2${
                              index === outcomes.length - 1 ? "" : " border-bottom"
                            }`}
                          >
                            <p className="text-dark d-flex align-items-center mb-0">
                              <i className="ti ti-circle-filled fs-8 me-1" style={{ color: outcome.color }} />
                              {outcome.label}
                            </p>
                            <p className="text-dark mb-0 text-end">
                              <span className="fw-semibold">{formatNumber(outcome.total.count)}</span>
                              <span className="text-muted ms-2">{formatCurrency(outcome.total.amount)}</span>
                            </p>
                          </div>
                        ))}
                      </div>
                      <div className="p-3 border rounded d-flex align-items-center justify-content-between mt-auto sud-win-rate">
                        <div>
                          <p className="mb-1">Win Rate</p>
                          <h4 className="mb-0">{winRate}%</h4>
                          <p className="fs-12 text-muted mb-0">
                            {formatNumber(scorecards.wonDeals.count)} won of {formatNumber(closedDeals)} closed
                          </p>
                        </div>
                        <span className="avatar avatar-lg rounded-circle nd-won-icon">
                          <i className="ti ti-trophy fs-24" />
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row nd-section">
            <div className="col-xl-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                    <SectionTitle title={`${pluralOf(stageKind, names)} by Stage`} />
                    <div className="d-flex align-items-center gap-2">
                      <MetricToggle value={stageMetric} onChange={setStageMetric} />
                      <OptionDropdown value={stageKind} options={recordOptions} onChange={setStageKind} />
                    </div>
                  </div>
                  {!loaded ? (
                    <TopDealsSkeleton />
                  ) : stages.length ? (
                    <div className="d-flex flex-column gap-3">
                      {stages.map((stage, index) => {
                        const value = stageValue(stage);
                        const width = stageMax > 0 && value > 0 ? Math.max(2, (value / stageMax) * 100) : 0;
                        const color = STAGE_COLORS[index % STAGE_COLORS.length];
                        return (
                          <div key={`${stage.stageId ?? stage.stageName}-${index}`}>
                            <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                              <p className="text-dark fw-medium mb-0 text-truncate" title={stage.stageName}>
                                {stage.stageName}
                              </p>
                              <p className="mb-0 text-nowrap">
                                <span className="text-dark fw-semibold">
                                  {stageMetric === "count" ? formatNumber(stage.count) : formatCurrency(stage.amount)}
                                </span>
                                <span className="text-muted ms-2">
                                  {stageMetric === "count"
                                    ? formatCurrency(stage.amount)
                                    : `${formatNumber(stage.count)} ${pluralOf(stageKind, names)}`}
                                </span>
                              </p>
                            </div>
                            <div className="sud-stage-track">
                              <div className="sud-stage-fill" style={{ width: `${width}%`, backgroundColor: color }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <EmptyState message={`No ${pluralOf(stageKind, names).toLowerCase()} in any stage yet`} />
                  )}
                </div>
              </div>
            </div>

            <div className="col-xl-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body d-flex flex-column">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                    <SectionTitle title="Recent Tasks" />
                    <Link className="btn btn-sm btn-light d-inline-flex align-items-center" to={route.tasks}>
                      View All<i className="ti ti-chevron-right ms-1" />
                    </Link>
                  </div>
                  {!loaded ? (
                    <TopDealsSkeleton />
                  ) : tasks.length ? (
                    <div className="d-flex flex-column">
                      {tasks.map((task, index) => (
                        <div
                          key={`${task.id ?? task.name}-${index}`}
                          className={`d-flex align-items-start gap-3 py-2${index === tasks.length - 1 ? "" : " border-bottom"}${
                            task.id !== null ? " sud-task-row" : ""
                          }`}
                          role={task.id !== null ? "button" : undefined}
                          tabIndex={task.id !== null ? 0 : undefined}
                          title={task.id !== null ? "View task details" : undefined}
                          onClick={task.id !== null ? () => openTask(task.id as number) : undefined}
                          onKeyDown={
                            task.id !== null
                              ? (event) => {
                                  if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    openTask(task.id as number);
                                  }
                                }
                              : undefined
                          }
                        >
                          <span className="sud-task-icon flex-shrink-0">
                            <i className="ti ti-checklist" />
                          </span>
                          <div className="flex-fill min-w-0">
                            <div className="d-flex align-items-center justify-content-between gap-2">
                              <p className="text-dark fw-medium mb-0 text-truncate" title={task.name}>
                                {task.name || "Untitled task"}
                              </p>
                              {task.status && (
                                <span className={`badge rounded-pill ${taskStatusClass(task.status)} flex-shrink-0`}>
                                  {task.status}
                                </span>
                              )}
                            </div>
                            {task.description && (
                              <p className="fs-13 text-muted mb-1 text-truncate" title={task.description}>
                                {task.description}
                              </p>
                            )}
                            <p className="fs-12 text-muted mb-0">
                              <i className="ti ti-calendar-due me-1" />
                              Due {formatDate(task.dueDate)}
                              {task.assignedBy && <span className="ms-2">· By {task.assignedBy}</span>}
                              {task.assignedTo && <span className="ms-2">· To {task.assignedTo}</span>}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState message="No recent tasks" />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row nd-section">
            <div className="col-md-12 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3 mb-3">
                    <SectionTitle title={`Recent ${pluralOf(recordKind, names)}`} />
                    <div className="d-flex align-items-center gap-2">
                      <OptionDropdown value={recordKind} options={recordOptions} onChange={setRecordKind} />
                      <Link
                        className="btn btn-sm btn-light d-inline-flex align-items-center"
                        to={RECORD_ROUTES[recordKind]}
                      >
                        View All<i className="ti ti-chevron-right ms-1" />
                      </Link>
                    </div>
                  </div>
                  <div className="table-responsive custom-table">
                    <table className="table table-bordered table-nowrap mb-0">
                      <thead className="table-white">
                        <tr>
                          <th>Name</th>
                          <th>Stage</th>
                          <th className="text-end">Amount</th>
                          {showSource && <th>Source</th>}
                          <th>Status</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!loaded ? (
                          <TableSkeletonRows columns={showSource ? 6 : 5} />
                        ) : records.length ? (
                          records.map((record, index) => (
                            <tr key={`${recordKind}-${record.id ?? index}`}>
                              <td>
                                <TruncatedLink
                                  to={record.id !== null ? `${RECORD_ROUTES[recordKind]}/${record.id}` : null}
                                  text={record.name || "Untitled"}
                                />
                              </td>
                              <td>{record.stage || "-"}</td>
                              <td className="text-end">{formatCurrency(record.amount)}</td>
                              {showSource && <td>{record.source || "-"}</td>}
                              <td>
                                {record.status ? (
                                  <span className={`badge rounded-pill ${statusClassName(record.status)}`}>
                                    {record.status}
                                  </span>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td>{formatDate(record.createdAt)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={showSource ? 6 : 5}>
                              <EmptyState message={`No recent ${pluralOf(recordKind, names).toLowerCase()}`} compact />
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {loaded && showTeams && (
            <div className="row nd-section">
              <div className="col-xxl-7 d-flex">
                <div className="card flex-fill">
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                      <SectionTitle title={isTeamManager ? "Team Performance" : "My Teams"} />
                    </div>
                    {teams.length ? (
                      <div className="table-responsive custom-table">
                        <table className="table table-bordered table-nowrap mb-0">
                          <thead className="table-white">
                            <tr>
                              <th rowSpan={2} className="align-middle">Team</th>
                              <th rowSpan={2} className="text-end align-middle">Members</th>
                              <th rowSpan={2} className="text-end align-middle">Revenue</th>
                              <th rowSpan={2} className="text-end align-middle">Sales</th>
                              <th colSpan={3} className="text-center sud-deals-group">
                                {names.deals}
                              </th>
                            </tr>
                            <tr>
                              <th className="text-center sud-deal-col">
                                <i className="ti ti-circle-filled fs-8 me-1" style={{ color: OUTCOME_COLORS.won }} />
                                Won
                              </th>
                              <th className="text-center sud-deal-col">
                                <i className="ti ti-circle-filled fs-8 me-1" style={{ color: OUTCOME_COLORS.open }} />
                                Open
                              </th>
                              <th className="text-center sud-deal-col">
                                <i className="ti ti-circle-filled fs-8 me-1" style={{ color: OUTCOME_COLORS.lost }} />
                                Lost
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {teams.map((team, index) => (
                              <tr key={`${team.teamId ?? team.teamName}-${index}`}>
                                <td>
                                  <p className="text-dark fw-medium mb-0">{team.teamName || "Unnamed Team"}</p>
                                  {team.managerName && (
                                    <small className="text-muted">Manager: {team.managerName}</small>
                                  )}
                                </td>
                                <td className="text-end">{formatNumber(team.members)}</td>
                                <td className="text-end">{formatCurrency(team.revenue)}</td>
                                <td className="text-end">{formatCurrency(team.sales)}</td>
                                <td className="text-center">
                                  <span className="sud-deal-count sud-deal-won">{formatNumber(team.wonDeals)}</span>
                                </td>
                                <td className="text-center">
                                  <span className="sud-deal-count sud-deal-open">{formatNumber(team.openDeals)}</span>
                                </td>
                                <td className="text-center">
                                  <span className="sud-deal-count sud-deal-lost">{formatNumber(team.lostDeals)}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <EmptyState message="No team performance data" compact />
                    )}
                  </div>
                </div>
              </div>
              <div className="col-xxl-5 d-flex">
                <div className="card flex-fill">
                  <div className="card-body pb-0">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-1">
                      <SectionTitle title="Team Activity" />
                      <MetricToggle value={teamMetric} onChange={setTeamMetric} />
                    </div>
                    {teamEntities.length && teamEntitySeries.length ? (
                      <Chart
                        key={teamMetric}
                        options={teamEntityOptions}
                        series={teamEntitySeries}
                        type="bar"
                        height={Math.max(240, teamEntities.length * 56 + 90)}
                      />
                    ) : (
                      <EmptyState
                        message={teamMetric === "count" ? "No team activity data" : "No team value data"}
                        compact
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <TaskDetailModal
        isVisible={taskModalOpen}
        taskDetails={taskDetails}
        isLoading={taskLoading}
        onClose={closeTask}
        onTaskUpdate={reload}
        tenantId={selectedTenantId}
      />
    </div>
  );
};

export default SalesUserDashboard;
